import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AtSign, KeyRound, Lock } from "lucide-react";
import { toast } from "sonner";
import AuthCard from "@/components/jea/AuthCard";
import JeaInput from "@/components/jea/JeaInput";
import JeaButton from "@/components/jea/JeaButton";
import { authApi, formatApiError } from "@/lib/apiClient";
import { cn } from "@/lib/utils";

/** Phase 10F — single identifier field with dual send + verify flow.
 *
 * Stage 1: `request` — user types email OR phone, we trigger send.
 * Stage 2: `verify` — user types the 6-digit code (SMS) or the token/code
 *          (email); user also sets a new password; we POST both.
 *
 * The success message is shown regardless of whether the identifier
 * actually resolves — prevents account enumeration.
 */
const ForgotPasswordPage = () => {
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [stage, setStage] = useState("request"); // "request" | "verify"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const identifierKind = () => {
    const t = identifier.trim();
    if (!t) return null;
    if (t.includes("@")) return "email";
    return "phone";
  };

  const normalizedIdentifier = () => {
    const t = identifier.trim();
    return t.includes("@") ? t.toLowerCase() : t;
  };

  const submitRequest = async (e) => {
    e?.preventDefault();
    setError("");
    if (!identifier.trim()) {
      setError("Enter your email or phone number.");
      return;
    }
    setLoading(true);
    try {
      await authApi.forgotPassword(normalizedIdentifier());
      setStage("verify");
      setCooldown(30);
      toast.success("Reset code sent (if the account exists).");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    if (code.length !== 6) {
      setError("Enter the 6-digit code from your email or SMS.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword({
        identifier: normalizedIdentifier(),
        code,
        new_password: newPassword,
      });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (stage === "request") {
    return (
      <AuthCard>
        <form onSubmit={submitRequest} className="space-y-5" data-testid="forgot-form">
          <div className="text-center space-y-2">
            <h2 className="text-[20px] font-bold text-white">Reset Your Password</h2>
            <p className="text-[14px] text-text-secondary">
              Enter your email or phone number — we'll send a 6-digit reset code.
            </p>
          </div>

          <JeaInput
            type="text"
            placeholder="Email or phone number"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            leftIcon={<AtSign size={16} />}
            autoComplete="username"
            data-testid="forgot-identifier-input"
          />
          {error && (
            <div
              className="text-[13px] text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2"
              data-testid="forgot-error"
            >
              {error}
            </div>
          )}
          <JeaButton
            type="submit"
            size="lg"
            fullWidth
            loading={loading}
            data-testid="forgot-submit-button"
          >
            Send Reset Code
          </JeaButton>

          <div className="text-center">
            <Link
              to="/login"
              className="text-[13px] text-text-secondary hover:text-success transition-colors"
              data-testid="forgot-back-to-login"
            >
              Back to login
            </Link>
          </div>
        </form>
      </AuthCard>
    );
  }

  // Stage 2 — enter code + new password
  const kind = identifierKind();
  return (
    <AuthCard>
      <form onSubmit={submitReset} className="space-y-4" data-testid="forgot-verify-form">
        <div className="space-y-2">
          <h2 className="text-[20px] font-bold text-white">
            Enter your reset code
          </h2>
          <p className="text-[13px] text-text-secondary">
            {kind === "phone"
              ? "We've texted a 6-digit code to your phone (if that account exists)."
              : "We've emailed a 6-digit code to your inbox (if that account exists). Check spam too."}{" "}
            It expires in 15 minutes.
          </p>
        </div>

        <div
          className="text-[12px] text-text-secondary bg-bg-tertiary/60 border border-border-default rounded-md px-3 py-2"
          data-testid="forgot-identifier-echo"
        >
          Account: <span className="text-white font-medium">{identifier}</span>
        </div>

        <JeaInput
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="6-digit code"
          value={code}
          maxLength={6}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          leftIcon={<KeyRound size={16} />}
          data-testid="forgot-code-input"
        />
        <JeaInput
          type="password"
          autoComplete="new-password"
          placeholder="New password (min 8 chars)"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          leftIcon={<Lock size={16} />}
          data-testid="forgot-new-password-input"
        />
        <JeaInput
          type="password"
          autoComplete="new-password"
          placeholder="Confirm new password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          leftIcon={<Lock size={16} />}
          data-testid="forgot-confirm-password-input"
        />

        {error && (
          <div
            className="text-[13px] text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2"
            data-testid="forgot-verify-error"
          >
            {error}
          </div>
        )}

        <JeaButton
          type="submit"
          size="lg"
          fullWidth
          loading={loading}
          data-testid="forgot-reset-submit-button"
        >
          Reset Password
        </JeaButton>

        <div className="flex items-center justify-between text-[12px]">
          <button
            type="button"
            onClick={() => cooldown === 0 && submitRequest()}
            disabled={cooldown > 0 || loading}
            className={cn(
              "text-text-secondary hover:text-success underline-offset-2",
              cooldown > 0 ? "opacity-50 cursor-not-allowed" : "hover:underline",
            )}
            data-testid="forgot-resend-button"
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
          </button>
          <button
            type="button"
            onClick={() => { setStage("request"); setCode(""); setError(""); }}
            className="text-text-secondary hover:text-success hover:underline underline-offset-2"
            data-testid="forgot-change-identifier"
          >
            Use a different account →
          </button>
        </div>
      </form>
    </AuthCard>
  );
};

export default ForgotPasswordPage;
